import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TopNavbar from '../../../components/TopNavbar';
import SideNavbar from '../../../components/SideNavbar';
import { collection, query, where, getDocs, doc, addDoc, updateDoc, Timestamp, orderBy, limit, getDoc, documentId } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { useAuth } from '../../../AuthContext';
import Toast from "../../../components/Toast";
import { useAppSelector } from '../../../redux/store';
import getCurrentScreen from "../../../Helper/GetScreen";

interface Task {
    id: string;
    task: string;
    userId: string;
    leadId: string;
    createdAt: Timestamp;
    dueDate: Timestamp;
    isDone: boolean;
    assignedBy?: string;  // ID of user who assigned the task
}

interface TaskWithLead extends Task {
    leadName?: string;
    leadNumber?: string;
    assignerName?: string;  // Name of the user who assigned the task
    assigneeName?: string;  // Name of the user the task is assigned to
}

interface User {
    id: string;
    companyId: string;
}

interface Lead {
    id: string;
    name: string;
    assignedTo: string;
    number: string;
    isActive: boolean;
}

interface UserData {
    name: string;
    companyId: string;
}

const Tasks = () => {

    const navigate = useNavigate();
    const { currentUser, isAdmin, isManager, isStaff } = useAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
    const [activeMyTasksTab, setActiveMyTasksTab] = useState<'today' | 'upcoming' | 'overdue'>('today');
    const [activeTeamTasksTab, setActiveTeamTasksTab] = useState<'today' | 'upcoming' | 'overdue'>('today');
    const [myTasks, setMyTasks] = useState<TaskWithLead[]>([]);
    const [teamTasks, setTeamTasks] = useState<TaskWithLead[]>([]);
    const [newTask, setNewTask] = useState("");
    const [newDueDate, setNewDueDate] = useState("");
    const [showAddTask, setShowAddTask] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [filterIsDone, setFilterIsDone] = useState<boolean | null>(null);
    const [leadId, setLeadId] = useState("");
    const [searchLeadQuery, setSearchLeadQuery] = useState("");
    const [editedTasks, setEditedTasks] = useState<{ [key: string]: string }>({});
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [showLeadDropdown, setShowLeadDropdown] = useState(false);
    const [loadingTaskIds, setLoadingTaskIds] = useState<string[]>([]);
    const [assignToOthers, setAssignToOthers] = useState(false);
    const [searchUserQuery, setSearchUserQuery] = useState("");
    const [selectedUserId, setSelectedUserId] = useState("");
    const [users, setUsers] = useState<Array<{ id: string, name: string }>>([]);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [searchResults, setSearchResults] = useState<Lead[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showMyTaskHistory, setShowMyTaskHistory] = useState(false);
    const [showTeamTaskHistory, setShowTeamTaskHistory] = useState(false);
    const [completedMyTasks, setCompletedMyTasks] = useState<TaskWithLead[]>([]);
    const [completedTeamTasks, setCompletedTeamTasks] = useState<TaskWithLead[]>([]);
    const [loadingMyTaskHistory, setLoadingMyTaskHistory] = useState(false);
    const [loadingTeamTaskHistory, setLoadingTeamTaskHistory] = useState(false);
    const filterPopupRef = React.createRef<HTMLDivElement>();
    const leadDropdownRef = React.createRef<HTMLDivElement>();
    const userDropdownRef = React.createRef<HTMLDivElement>();
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [leadNames, setLeadNames] = useState<{ [leadId: string]: string }>({});

    const categorizeTasksByDueDate = (tasks: TaskWithLead[]) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        return {
            dueToday: tasks.filter(task => {
                const dueDate = task.dueDate.toDate();
                return dueDate >= today && dueDate < tomorrow;
            }),
            upcoming: tasks.filter(task => {
                const dueDate = task.dueDate.toDate();
                return dueDate >= tomorrow;
            }),
            overdue: tasks.filter(task => {
                const dueDate = task.dueDate.toDate();
                return dueDate < today;
            })
        };
    };

    const fetchTaskLeads = async (tasks: Task[]) => {
        if (!currentUser || tasks.length === 0) return;

        try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            // Get unique lead IDs from tasks
            const leadIds = Object.keys(
                tasks.reduce((acc: { [key: string]: boolean }, task) => {
                    acc[task.leadId] = true;
                    return acc;
                }, {})
            ).filter(leadId => leadId && leadId.trim() !== ''); // Filter out empty or invalid lead IDs

            // Get unique user IDs (both assigners and assignees)
            const userIds = Object.keys(
                tasks.reduce((acc: { [key: string]: boolean }, task) => {
                    if (task.assignedBy) {
                        acc[task.assignedBy] = true;
                    }
                    acc[task.userId] = true;
                    return acc;
                }, {})
            ).filter(userId => userId && userId.trim() !== ''); // Filter out empty or invalid user IDs

            // Fetch all leads and users in parallel
            const [leadDocs, userDocs] = await Promise.all([
                Promise.all(
                    leadIds.map(async (leadId) => {
                        try {
                            return await getDoc(doc(db, `companies/${userData.companyId}/leads`, leadId));
                        } catch (error) {
                            console.warn(`Failed to fetch lead ${leadId}:`, error);
                            return null;
                        }
                    })
                ),
                Promise.all(
                    userIds.map(async (userId) => {
                        try {
                            return await getDoc(doc(db, 'users', userId));
                        } catch (error) {
                            console.warn(`Failed to fetch user ${userId}:`, error);
                            return null;
                        }
                    })
                )
            ]);

            const fetchedLeads = leadDocs
                .filter(doc => doc && doc.exists())
                .map(doc => ({ id: doc!.id, ...doc!.data() } as Lead));

            const userNames = userDocs
                .filter(doc => doc && doc.exists())
                .reduce((acc: { [key: string]: string }, doc) => {
                    if (doc) {
                        acc[doc.id] = (doc.data() as UserData).name;
                    }
                    return acc;
                }, {});

            // Update tasks with lead and user information
            const updatedTasks: TaskWithLead[] = tasks.map(task => {
                const lead = fetchedLeads.find(l => l.id === task.leadId);
                return {
                    ...task,
                    leadName: lead?.name,
                    leadNumber: lead?.number,
                    assignerName: task.assignedBy ? userNames[task.assignedBy] : undefined,
                    assigneeName: userNames[task.userId]
                };
            });

            return updatedTasks;
        } catch (error) {
            console.error('Error fetching task leads:', error);
        }
    };

    const fetchTasks = async () => {
        if (!currentUser) return;

        try {
            setIsLoading(true);
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            // Fetch pending my tasks (assigned to me)
            let myTasksQuery = query(
                collection(db, `companies/${userData.companyId}/tasks`),
                where('userId', '==', currentUser.uid),
                where('isDone', '==', false)
            );

            // Fetch pending team tasks (assigned by me to others)
            let teamTasksQuery = query(
                collection(db, `companies/${userData.companyId}/tasks`),
            );

            if (isAdmin && currentScreen === "Admin") {
                teamTasksQuery = query(
                    teamTasksQuery,
                    where('isDone', '==', false)
                );
            } else if (isManager && currentScreen === "Manager") {
                const usersQuery = query(
                    collection(db, "users"),
                    where("isActive", "==", true),
                    where("companyId", "==", userData?.companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );
                const usersSnapshot = await getDocs(usersQuery);
                const userIds = usersSnapshot.docs.map(doc => doc.id);

                teamTasksQuery = query(
                    teamTasksQuery,
                    where('assignedBy', 'in', userIds),
                    where('isDone', '==', false)
                );
            } else if (isStaff && currentScreen === "Staff") {
                teamTasksQuery = query(
                    teamTasksQuery,
                    where('assignedBy', '==', currentUser.uid),
                    where('isDone', '==', false)
                );
            }

            const [myTasksSnapshot, teamTasksSnapshot] = await Promise.all([
                getDocs(myTasksQuery),
                getDocs(teamTasksQuery)
            ]);

            const fetchedMyTasks = myTasksSnapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id,
            } as Task));

            const fetchedTeamTasks = teamTasksSnapshot.docs
                .map(doc => ({
                    ...doc.data(),
                    id: doc.id,
                } as Task))
                .filter(task => task.userId !== currentUser.uid);

            const [updatedMyTasks, updatedTeamTasks] = await Promise.all([
                fetchTaskLeads(fetchedMyTasks),
                fetchTaskLeads(fetchedTeamTasks)
            ]);

            setMyTasks(updatedMyTasks || []);
            setTeamTasks(updatedTeamTasks || []);
        } catch (error) {
            console.error('Error fetching tasks:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchTaskHistory = async (isMyTasks: boolean) => {
        if (!currentUser) return;

        try {
            if (isMyTasks) {
                setLoadingMyTaskHistory(true);
            } else {
                setLoadingTeamTaskHistory(true);
            }

            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            // Calculate date 7 days ago
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
            sevenDaysAgo.setHours(0, 0, 0, 0);

            // Fetch completed tasks from last 7 days
            let historyQuery = query(
                collection(db, `companies/${userData.companyId}/tasks`),
            );

            if (isAdmin && currentScreen === "Admin") {
                historyQuery = query(
                    historyQuery,
                    where(isMyTasks ? 'userId' : 'assignedBy', '==', currentUser.uid),
                    where('isDone', '==', true),
                    where('dueDate', '>=', Timestamp.fromDate(sevenDaysAgo)),
                    orderBy('dueDate', 'desc')
                );
            } else if (isManager && currentScreen === "Manager") {
                const usersQuery = query(
                    collection(db, "users"),
                    where("isActive", "==", true),
                    where("companyId", "==", userData?.companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );
                const usersSnapshot = await getDocs(usersQuery);
                const userIds = usersSnapshot.docs.map(doc => doc.id);

                historyQuery = query(
                    historyQuery,
                    where(isMyTasks ? 'userId' : 'assignedBy', isMyTasks ? '==' : 'in', isMyTasks ? currentUser.uid : userIds),
                    where('isDone', '==', true),
                    where('dueDate', '>=', Timestamp.fromDate(sevenDaysAgo)),
                    orderBy('dueDate', 'desc')
                );
            } else if (isStaff && currentScreen === "Staff") {
                historyQuery = query(
                    historyQuery,
                    where(isMyTasks ? 'userId' : 'assignedBy', '==', currentUser.uid),
                    where('isDone', '==', true),
                    where('dueDate', '>=', Timestamp.fromDate(sevenDaysAgo)),
                    orderBy('dueDate', 'desc')
                );
            }

            const historySnapshot = await getDocs(historyQuery);
            const fetchedHistory = historySnapshot.docs.map(doc => ({
                ...doc.data(),
                id: doc.id,
            } as Task));

            const updatedHistory = await fetchTaskLeads(fetchedHistory);

            if (isMyTasks) {
                setCompletedMyTasks(updatedHistory || []);
            } else {
                setCompletedTeamTasks(updatedHistory || []);
            }
        } catch (error) {
            console.error('Error fetching task history:', error);
        } finally {
            if (isMyTasks) {
                setLoadingMyTaskHistory(false);
            } else {
                setLoadingTeamTaskHistory(false);
            }
        }
    };

    useEffect(() => {
        if (currentUser) {
            fetchTasks();
        }
    }, [currentUser, filterIsDone]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
                setShowFilter(false);
            }
            if (leadDropdownRef.current && !leadDropdownRef.current.contains(event.target as Node)) {
                setShowLeadDropdown(false);
            }
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
                setShowUserDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const searchLeads = async (searchQuery: string) => {
        if (!currentUser || searchQuery.length < 3) {
            setLeadId("");
            setSearchResults([]);
            return;
        }

        try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            const upperQuery = searchQuery.toUpperCase();
            const leadsRef = collection(db, `companies/${userData.companyId}/leads`);

            // Search by name (case-insensitive, starts with)
            let nameQuery = query(
                leadsRef,
            );

            // Search by number (exact match)
            let numberQuery = query(
                leadsRef,
            );

            if (isAdmin && currentScreen === "Admin") {
                nameQuery = query(
                    leadsRef,
                    where('isActive', '==', true),
                    where('name', '>=', upperQuery),
                    where('name', '<=', upperQuery + '\uf8ff')
                );

                numberQuery = query(
                    leadsRef,
                    where('assignedTo', '==', currentUser.uid),
                    where('isActive', '==', true),
                    where('number', '==', searchQuery)
                );

            } else if (isManager && currentScreen === "Manager") {
                const usersQuery = query(
                    collection(db, "users"),
                    where("isActive", "==", true),
                    where("companyId", "==", userData?.companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );

                const usersSnapshot = await getDocs(usersQuery);
                const userIds = usersSnapshot.docs.map(doc => doc.id);

                nameQuery = query(
                    leadsRef,
                    where('assignedTo', 'in', userIds),
                    where('isActive', '==', true),
                    where('name', '>=', upperQuery),
                    where('name', '<=', upperQuery + '\uf8ff')
                );

                numberQuery = query(
                    leadsRef,
                    where('assignedTo', 'in', userIds),
                    where('isActive', '==', true),
                    where('number', '==', searchQuery)
                );

            } else if (isStaff && currentScreen === "Staff") {
                nameQuery = query(
                    leadsRef,
                    where('assignedTo', '==', currentUser.uid),
                    where('isActive', '==', true),
                    where('name', '>=', upperQuery),
                    where('name', '<=', upperQuery + '\uf8ff')
                );

                numberQuery = query(
                    leadsRef,
                    where('assignedTo', '==', currentUser.uid),
                    where('isActive', '==', true),
                    where('number', '==', searchQuery)
                );

            }

            const [nameSnapshot, numberSnapshot] = await Promise.all([
                getDocs(nameQuery),
                getDocs(numberQuery)
            ]);

            // Combine results, removing duplicates
            const fetchedLeads: Lead[] = [];
            const seenIds = new Set<string>();

            nameSnapshot.forEach(doc => {
                if (!seenIds.has(doc.id)) {
                    seenIds.add(doc.id);
                    fetchedLeads.push({ id: doc.id, ...doc.data() } as Lead);
                }
            });

            numberSnapshot.forEach(doc => {
                if (!seenIds.has(doc.id)) {
                    seenIds.add(doc.id);
                    fetchedLeads.push({ id: doc.id, ...doc.data() } as Lead);
                }
            });

            setSearchResults(fetchedLeads);
        } catch (error) {
            console.error('Error searching leads:', error);
            setSearchResults([]);
        }
    };

    useEffect(() => {
        if (searchLeadQuery.length >= 3) {
            searchLeads(searchLeadQuery);
        } else {
            setLeadId("");
            setSearchResults([]);
        }
    }, [searchLeadQuery]);

    const searchUsers = async (searchQuery: string) => {
        if (!currentUser || searchQuery.length < 3) {
            setUsers([]);
            return;
        }

        try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            const upperQuery = searchQuery.toUpperCase();
            const usersRef = collection(db, 'users');
            const usersQuery = query(
                usersRef,
                where('companyId', '==', userData.companyId),
                where('name', '>=', upperQuery),
                where('name', '<=', upperQuery + '\uf8ff')
            );

            const usersSnapshot = await getDocs(usersQuery);
            const fetchedUsers = usersSnapshot.docs
                .filter(doc => doc.data().isActive !== false) // Filter out deactivated users
                .map(doc => ({
                    id: doc.id,
                    name: (doc.data() as UserData).name
                }));
            setUsers(fetchedUsers);
        } catch (error) {
            console.error('Error searching users:', error);
        }
    };

    useEffect(() => {
        if (searchUserQuery.length >= 3) {
            searchUsers(searchUserQuery);
        } else {
            setUsers([]);
        }
    }, [searchUserQuery]);

    const addTask = async () => {
        if (!currentUser || !newTask || !newDueDate || !leadId) return;

        try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            const taskData = {
                task: newTask,
                userId: assignToOthers ? selectedUserId : currentUser.uid,
                leadId: leadId,
                createdAt: Timestamp.now(),
                dueDate: Timestamp.fromDate(new Date(newDueDate)),
                isDone: false,
                assignedBy: currentUser.uid
            };

            await addDoc(collection(db, `companies/${userData.companyId}/tasks`), taskData);

            // Reset form
            setNewTask("");
            setNewDueDate("");
            setLeadId("");
            setSearchLeadQuery("");
            setShowAddTask(false);
            setAssignToOthers(false);
            setSelectedUserId("");
            setSearchUserQuery("");

            // Refresh tasks
            await fetchTasks();

            setToastMessage('Task added successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            console.error('Error adding task:', error);
            setToastMessage('Error adding task. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        }
    };

    const toggleTaskStatus = async (taskId: string) => {
        if (!currentUser) return;

        try {
            setLoadingTaskIds(prev => [...prev, taskId]);

            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            const taskRef = doc(db, `companies/${userData.companyId}/tasks`, taskId);
            // Check in all possible task lists
            const task = [...myTasks, ...teamTasks, ...completedMyTasks, ...completedTeamTasks].find(t => t.id === taskId);
            if (!task) return;

            const newIsDone = !task.isDone;
            await updateDoc(taskRef, {
                isDone: newIsDone
            });

            // Update the task in all lists where it exists
            // For My Tasks
            setMyTasks(prevTasks =>
                prevTasks.map(t =>
                    t.id === taskId
                        ? { ...t, isDone: newIsDone }
                        : t
                )
            );

            // For Team Tasks
            setTeamTasks(prevTasks =>
                prevTasks.map(t =>
                    t.id === taskId
                        ? { ...t, isDone: newIsDone }
                        : t
                )
            );

            // For Completed My Tasks
            setCompletedMyTasks(prevTasks => {
                // If task exists in this list, update it
                if (prevTasks.some(t => t.id === taskId)) {
                    return prevTasks.map(t =>
                        t.id === taskId
                            ? { ...t, isDone: newIsDone }
                            : t
                    );
                }
                // If task was just completed and belongs in my tasks, add it
                else if (newIsDone && task.userId === currentUser.uid) {
                    return [...prevTasks, { ...task, isDone: true }];
                }
                return prevTasks;
            });

            // For Completed Team Tasks
            setCompletedTeamTasks(prevTasks => {
                // If task exists in this list, update it
                if (prevTasks.some(t => t.id === taskId)) {
                    return prevTasks.map(t =>
                        t.id === taskId
                            ? { ...t, isDone: newIsDone }
                            : t
                    );
                }
                // If task was just completed and belongs in team tasks, add it
                else if (newIsDone && task.userId !== currentUser.uid) {
                    return [...prevTasks, { ...task, isDone: true }];
                }
                return prevTasks;
            });

        } catch (error) {
            console.error('Error toggling task status:', error);
        } finally {
            setLoadingTaskIds(prev => prev.filter(id => id !== taskId));
        }
    };

    const updateTask = async (taskId: string, originalTask: Task) => {
        if (!currentUser) return;

        try {
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            const userData = userDoc.data() as User | undefined;
            if (!userData?.companyId) return;

            const taskRef = doc(db, `companies/${userData.companyId}/tasks`, taskId);
            await updateDoc(taskRef, {
                task: editedTasks[taskId],
                isDone: originalTask.isDone ? false : originalTask.isDone // If it was done, set to false, otherwise keep current state
            });

            // Clear the edited state for this task
            setEditedTasks(prev => {
                const newState = { ...prev };
                delete newState[taskId];
                return newState;
            });

            fetchTasks();
        } catch (error) {
            console.error('Error updating task:', error);
        }
    };

    const handleTaskEdit = (taskId: string, value: string) => {
        setEditedTasks(prev => ({
            ...prev,
            [taskId]: value
        }));
    };

    const isTaskEdited = (taskId: string) => {
        return editedTasks[taskId] !== undefined;
    };

    const getFilteredTasks = () => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);

        let filteredByDate = [...myTasks, ...teamTasks];

        // First filter by date based on activeSection
        switch (activeMyTasksTab) {
            case "today":
                filteredByDate = filteredByDate.filter(task => {
                    const dueDate = task.dueDate.toDate();
                    dueDate.setHours(0, 0, 0, 0);
                    return dueDate.getTime() === now.getTime();
                });
                break;
            case "upcoming":
                filteredByDate = filteredByDate.filter(task => {
                    const dueDate = task.dueDate.toDate();
                    dueDate.setHours(0, 0, 0, 0);
                    return dueDate.getTime() > now.getTime();
                });
                break;
            case "overdue":
                filteredByDate = filteredByDate.filter(task => {
                    const dueDate = task.dueDate.toDate();
                    dueDate.setHours(0, 0, 0, 0);
                    return dueDate.getTime() < now.getTime();
                });
                break;
            default:
                break;
        }

        // Then filter by completion status
        if (selectedFilters.length === 0 || selectedFilters.length === 2) {
            return filteredByDate;
        } else if (selectedFilters.includes('Done')) {
            return filteredByDate.filter(task => task.isDone);
        } else {
            return filteredByDate.filter(task => !task.isDone);
        }
    };

    const filteredTasks = getFilteredTasks();

    const handleFilterChange = (value: string) => {
        setSelectedFilters(prev =>
            prev.includes(value)
                ? prev.filter(item => item !== value)
                : [...prev, value]
        );
    };

    const handleStudentClick = (studentId: string) => {
        navigate(`/studentdetails/${studentId}`);
    };

    // get lead's name by id from firestore leads collection from companies collection
    const getLeadNameById = async (leadId: string, companyId: string) => {
        const leadQuery = query(
            collection(db, 'companies', companyId, 'leads'),
            where(documentId(), '==', leadId)
        );
        return getDocs(leadQuery).then(snapshot => {
            if (!snapshot.empty) {
                return snapshot.docs[0].data().name;
            } else {
                return 'Unknown';
            }
        });
    };

    // Helper to fetch and cache lead name
    const fetchAndCacheLeadName = async (leadId: string, companyId: string) => {
        if (!leadId || leadNames[leadId]) return;
        const name = await getLeadNameById(leadId, companyId);
        setLeadNames(prev => ({ ...prev, [leadId]: name }));
    };

    const TaskItem = ({ task, isTeamTask = false }: { task: TaskWithLead, isTeamTask?: boolean }) => {
        useEffect(() => {
            if (!task.leadName && task.leadId && currentUser) {
                getDoc(doc(db, 'users', currentUser.uid)).then(userDoc => {
                    const userData = userDoc.data() as User | undefined;
                    if (userData?.companyId) {
                        fetchAndCacheLeadName(task.leadId, userData.companyId);
                    }
                });
            }
        }, [task.leadId, task.leadName, currentUser]);

        const displayLeadName = task.leadName || leadNames[task.leadId] || 'Unknown';

        return (
            <div className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-md ${task.isDone ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start gap-4">
                    <div className="flex-1">
                        <textarea
                            value={editedTasks[task.id] !== undefined ? editedTasks[task.id] : task.task}
                            onChange={(e) => handleTaskEdit(task.id, e.target.value)}
                            className={`w-full p-2 text-sm rounded-lg resize-none transition-colors duration-200 ${task.isDone
                                ? 'text-gray-500 bg-gray-50 border-gray-200'
                                : 'text-gray-700 bg-white border-gray-300'
                                } focus:ring-2 focus:ring-cyan-500 focus:border-transparent`}
                            rows={2}
                        />
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                            <div className="flex items-center gap-1 text-gray-600">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {isTeamTask ? (
                                    <span>Assigned to: <span className="font-medium text-gray-900">{task.assigneeName || 'Unknown'}</span></span>
                                ) : (
                                    <span>Assigned by: <span className="font-medium text-gray-900">
                                        {task.userId === task.assignedBy ? 'Self' : task.assignerName || 'Unknown'}
                                    </span></span>
                                )}
                            </div>
                            <div className="flex items-center gap-1 text-gray-600">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span
                                    onClick={() => handleStudentClick(task.leadId)}
                                    className="cursor-pointer"
                                >Lead: <span className="font-medium text-gray-900">{displayLeadName}</span></span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-600">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>Due: <span className="font-medium text-gray-900">{task.dueDate.toDate().toLocaleString()}</span></span>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={() => isTaskEdited(task.id) ? updateTask(task.id, task) : toggleTaskStatus(task.id)}
                        disabled={loadingTaskIds.includes(task.id)}
                        className={`px-4 py-2 rounded-lg text-white shadow-sm hover:shadow-md transition-all duration-200 ${loadingTaskIds.includes(task.id)
                            ? 'bg-gray-400 cursor-not-allowed'
                            : isTaskEdited(task.id)
                                ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700'
                                : task.isDone
                                    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                                    : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
                        }`}
                    >
                        {loadingTaskIds.includes(task.id) ? (
                            <div className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="text-sm">
                                    {task.isDone ? 'Setting Pending...' : 'Setting Done...'}
                                </span>
                            </div>
                        ) : (
                            <span className="text-sm">
                                {isTaskEdited(task.id) ? 'Save Changes' : `Mark as ${task.isDone ? 'Pending' : 'Done'}`}
                            </span>
                        )}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-200">
            {/* <div className="fixed top-0 left-0 w-full z-20">
                <TopNavbar />
            </div> */}

            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md w-full min-h-screen flex flex-col">
                    <div className="flex items-center gap-6 mb-8">
                        <div className="flex items-center justify-center w-14 h-14 rounded-full" style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)' }}>
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/hugeicons_task-edit-01.svg`} alt="Lead Icon" />
                        </div>
                        <h2 className="text-2xl font-medium text-black">My Tasks</h2>
                        <div className="ml-auto flex items-center gap-4">
                            <button
                                onClick={() => setShowAddTask(true)}
                                className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                                style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)' }}
                            >
                                <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px' }}>Add Task</span>
                                <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4 ml-2" />
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-1 space-x-8 p-8">
                        {/* My Tasks Section */}
                        <div className="flex-1 bg-white rounded-2xl p-6 shadow-sm border-2 border-gray-200">

                            <div className="mb-8">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h1 className="text-2xl font-Montserrat text-gray-800 font-bold">My Tasks</h1>
                                        <p className="text-sm text-gray-500 mt-1">Tasks assigned to you</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setShowMyTaskHistory(prev => !prev);
                                            if (!completedMyTasks.length) {
                                                fetchTaskHistory(true);
                                            }
                                        }}
                                        className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        View History
                                    </button>
                                </div>

                                {/* History Dropdown */}
                                {showMyTaskHistory && (
                                    <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-700 mb-4">
                                            Completed Tasks History
                                            <span className="text-sm font-normal text-gray-500 ml-2">(Last 7 days)</span>
                                        </h3>
                                        {loadingMyTaskHistory ? (
                                            <div className="flex justify-center py-4">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                                            </div>
                                        ) : completedMyTasks.length === 0 ? (
                                            <p className="text-gray-500 text-center py-4">No completed tasks in the last 7 days</p>
                                        ) : (
                                            <div className="space-y-4">
                                                {completedMyTasks.map(task => (
                                                    <TaskItem key={task.id} task={task} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Tabs */}
                            <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                                <button
                                    onClick={() => setActiveMyTasksTab('today')}
                                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${activeMyTasksTab === 'today'
                                        ? 'bg-white text-cyan-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Due Today ({categorizeTasksByDueDate(myTasks).dueToday.length})
                                </button>
                                <button
                                    onClick={() => setActiveMyTasksTab('upcoming')}
                                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${activeMyTasksTab === 'upcoming'
                                        ? 'bg-white text-cyan-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Upcoming ({categorizeTasksByDueDate(myTasks).upcoming.length})
                                </button>
                                <button
                                    onClick={() => setActiveMyTasksTab('overdue')}
                                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${activeMyTasksTab === 'overdue'
                                        ? 'bg-white text-cyan-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Overdue ({categorizeTasksByDueDate(myTasks).overdue.length})
                                </button>
                            </div>

                            {/* Task Content */}
                            {isLoading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {activeMyTasksTab === 'today' && (
                                        <>
                                            {categorizeTasksByDueDate(myTasks).dueToday.map(task => (
                                                <TaskItem key={task.id} task={task} />
                                            ))}
                                            {categorizeTasksByDueDate(myTasks).dueToday.length === 0 && (
                                                <div className="text-center py-8 bg-gray-50 rounded-lg">
                                                    <p className="text-gray-500">No tasks due today</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {activeMyTasksTab === 'upcoming' && (
                                        <>
                                            {categorizeTasksByDueDate(myTasks).upcoming.map(task => (
                                                <TaskItem key={task.id} task={task} />
                                            ))}
                                            {categorizeTasksByDueDate(myTasks).upcoming.length === 0 && (
                                                <div className="text-center py-8 bg-gray-50 rounded-lg">
                                                    <p className="text-gray-500">No upcoming tasks</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {activeMyTasksTab === 'overdue' && (
                                        <>
                                            {categorizeTasksByDueDate(myTasks).overdue.map(task => (
                                                <TaskItem key={task.id} task={task} />
                                            ))}
                                            {categorizeTasksByDueDate(myTasks).overdue.length === 0 && (
                                                <div className="text-center py-8 bg-gray-50 rounded-lg">
                                                    <p className="text-gray-500">No overdue tasks</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Team Tasks Section */}
                        <div className="flex-1 bg-white rounded-2xl p-6 shadow-sm border-2 border-gray-200">
                            <div className="mb-8">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h1 className="text-2xl font-Montserrat text-gray-800 font-bold">Team Tasks</h1>
                                        <p className="text-sm text-gray-500 mt-1">Tasks you assigned to others</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setShowTeamTaskHistory(prev => !prev);
                                            if (!completedTeamTasks.length) {
                                                fetchTaskHistory(false);
                                            }
                                        }}
                                        className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        View History
                                    </button>
                                </div>

                                {/* History Dropdown */}
                                {showTeamTaskHistory && (
                                    <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                                        <h3 className="text-lg font-semibold text-gray-700 mb-4">
                                            Completed Tasks History
                                            <span className="text-sm font-normal text-gray-500 ml-2">(Last 7 days)</span>
                                        </h3>
                                        {loadingTeamTaskHistory ? (
                                            <div className="flex justify-center py-4">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                                            </div>
                                        ) : completedTeamTasks.length === 0 ? (
                                            <p className="text-gray-500 text-center py-4">No completed tasks in the last 7 days</p>
                                        ) : (
                                            <div className="space-y-4">
                                                {completedTeamTasks.map(task => (
                                                    <TaskItem key={task.id} task={task} isTeamTask={true} />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Tabs */}
                            <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
                                <button
                                    onClick={() => setActiveTeamTasksTab('today')}
                                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${activeTeamTasksTab === 'today'
                                        ? 'bg-white text-cyan-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Due Today ({categorizeTasksByDueDate(teamTasks).dueToday.length})
                                </button>
                                <button
                                    onClick={() => setActiveTeamTasksTab('upcoming')}
                                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${activeTeamTasksTab === 'upcoming'
                                        ? 'bg-white text-cyan-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Upcoming ({categorizeTasksByDueDate(teamTasks).upcoming.length})
                                </button>
                                <button
                                    onClick={() => setActiveTeamTasksTab('overdue')}
                                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${activeTeamTasksTab === 'overdue'
                                        ? 'bg-white text-cyan-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Overdue ({categorizeTasksByDueDate(teamTasks).overdue.length})
                                </button>
                            </div>

                            {/* Task Content */}
                            {isLoading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {activeTeamTasksTab === 'today' && (
                                        <>
                                            {categorizeTasksByDueDate(teamTasks).dueToday.map(task => (
                                                <TaskItem key={task.id} task={task} isTeamTask={true} />
                                            ))}
                                            {categorizeTasksByDueDate(teamTasks).dueToday.length === 0 && (
                                                <div className="text-center py-8 bg-gray-50 rounded-lg">
                                                    <p className="text-gray-500">No tasks due today</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {activeTeamTasksTab === 'upcoming' && (
                                        <>
                                            {categorizeTasksByDueDate(teamTasks).upcoming.map(task => (
                                                <TaskItem key={task.id} task={task} isTeamTask={true} />
                                            ))}
                                            {categorizeTasksByDueDate(teamTasks).upcoming.length === 0 && (
                                                <div className="text-center py-8 bg-gray-50 rounded-lg">
                                                    <p className="text-gray-500">No upcoming tasks</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {activeTeamTasksTab === 'overdue' && (
                                        <>
                                            {categorizeTasksByDueDate(teamTasks).overdue.map(task => (
                                                <TaskItem key={task.id} task={task} isTeamTask={true} />
                                            ))}
                                            {categorizeTasksByDueDate(teamTasks).overdue.length === 0 && (
                                                <div className="text-center py-8 bg-gray-50 rounded-lg">
                                                    <p className="text-gray-500">No overdue tasks</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {showAddTask && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-montserrat text-gray-900">Add New Task</h2>
                                    <button onClick={() => setShowAddTask(false)}>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/close.svg`}
                                            alt="Close"
                                            className="w-6 h-6"
                                        />
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Task Description</label>
                                        <textarea
                                            value={newTask}
                                            onChange={(e) => setNewTask(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                            rows={3}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Lead</label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={searchLeadQuery}
                                                onChange={(e) => {
                                                    setSearchLeadQuery(e.target.value);
                                                    setShowLeadDropdown(true);
                                                }}
                                                onClick={() => setShowLeadDropdown(true)}
                                                placeholder="Search lead by name or number"
                                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                            />
                                            {showLeadDropdown && searchLeadQuery && (
                                                <div
                                                    ref={leadDropdownRef}
                                                    className="absolute w-full mt-1 max-h-60 overflow-auto bg-white border border-gray-300 rounded-lg shadow-lg z-50"
                                                >
                                                    {searchLeadQuery.length >= 3 ? (
                                                        <>
                                                            {searchResults.length > 0 ? (
                                                                searchResults.map((lead) => (
                                                                    <button
                                                                        key={lead.id}
                                                                        onClick={() => {
                                                                            setLeadId(lead.id);
                                                                            setSearchLeadQuery(lead.name);
                                                                            setShowLeadDropdown(false);
                                                                        }}
                                                                        className="w-full px-4 py-2 text-left hover:bg-gray-50"
                                                                    >
                                                                        <div>{lead.name}</div>
                                                                        <div className="text-sm text-gray-500">{lead.number}</div>
                                                                    </button>
                                                                ))
                                                            ) : (
                                                                <div className="px-4 py-2 text-gray-500">
                                                                    No leads found
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="px-4 py-2 text-gray-500">
                                                            Type at least 3 characters to search
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                                        <input
                                            type="datetime-local"
                                            value={newDueDate}
                                            onChange={(e) => setNewDueDate(e.target.value)}
                                            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Assign to</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="radio"
                                                checked={!assignToOthers}
                                                onChange={() => setAssignToOthers(false)}
                                                className="form-radio h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                                            />
                                            <span className="text-sm text-gray-700">Myself</span>
                                            <input
                                                type="radio"
                                                checked={assignToOthers}
                                                onChange={() => setAssignToOthers(true)}
                                                className="form-radio h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                                            />
                                            <span className="text-sm text-gray-700">Others</span>
                                        </div>
                                        {assignToOthers && (
                                            <div className="relative mt-2">
                                                <input
                                                    type="text"
                                                    value={searchUserQuery}
                                                    onChange={(e) => {
                                                        setSearchUserQuery(e.target.value);
                                                        setShowUserDropdown(true);
                                                    }}
                                                    onClick={() => setShowUserDropdown(true)}
                                                    placeholder="Search user by name"
                                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                                                />
                                                {showUserDropdown && searchUserQuery && (
                                                    <div
                                                        ref={userDropdownRef}
                                                        className="absolute w-full mt-1 max-h-60 overflow-auto bg-white border border-gray-300 rounded-lg shadow-lg z-50"
                                                    >
                                                        {users.length > 0 ? (
                                                            users.map((user) => (
                                                                <button
                                                                    key={user.id}
                                                                    onClick={() => {
                                                                        setSelectedUserId(user.id);
                                                                        setSearchUserQuery(user.name);
                                                                        setShowUserDropdown(false);
                                                                    }}
                                                                    className="w-full px-4 py-2 text-left hover:bg-gray-50"
                                                                >
                                                                    <div>{user.name}</div>
                                                                </button>
                                                            ))
                                                        ) : (
                                                            <div className="px-4 py-2 text-gray-500">
                                                                {searchUserQuery.length < 3
                                                                    ? "Type at least 3 characters to search"
                                                                    : "No users found"}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex justify-end gap-4 mt-6">
                                        <button
                                            onClick={() => setShowAddTask(false)}
                                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-full hover:bg-gray-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={addTask}
                                            className="px-4 py-2 text-white rounded-full"
                                            style={{ background: 'linear-gradient(180deg, #3483BE 0%, #02385E 100%)' }}
                                        >
                                            Add Task
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default Tasks;